// Portfolio Tracker display resolver — maps raw CoreAPI `OrderStatus` values into
// the 6 display stages the storefront UI renders. The resolver lives here (and
// not on the Sunny16 side) because the display bucketing is a presentation
// concern: parent-portal and the storefront each have their own bucketings for
// the same underlying enum. See
// `docs/features/2026.07.06-senior-market/2026.05.26-001-sunny16-portfolio-tracker-integration-spec.md`
// for the ownership rationale.

import { appErrorService } from '@/services/AppErrorService'

import { ContextKey } from '@/types/ContextKey'

import type { SeniorPortfolioDetail } from '@VelocitySquared/cady-sunny16-commerce-integration-models/core-api'
import { OrderStatus } from '@VelocitySquared/cady-sunny16-commerce-integration-models/core-api'
import type {
  BundleStorefrontDto,
  CatalogBundleStorefrontDto
} from '@VelocitySquared/sunny16-commerce-dataobjects/storefront'

// Narrowed alias for catalog bundles where the underlying `bundle` is known
// to be populated (not null). `findAutoAddPortfolioBundle`'s filter implicitly
// guarantees this — declaring it in the return type lets the callsite skip a
// redundant null check on `standalone.bundle`.
export type CatalogBundleWithResolvedBundle = CatalogBundleStorefrontDto & {
  bundle: BundleStorefrontDto
}

// Sunny16 bundle-attribute keys/values that drive Portfolio Tracker cart
// reconciliation. Sourced from the Sunny16 catalog config — see iteration 9 of
// `docs/features/2026.07.06-senior-market/implementation-plans/2026.05.26-001-portfolio-tracker.md`.
export const PORTFOLIO_TYPE_ATTRIBUTE_KEY = 'CADY_PortfolioType'
export const SENIOR_PORTFOLIO_ATTRIBUTE_KEY = 'CADY_Senior_Portfolio'
export const SENIOR_PORTFOLIO_AUTO_ADD_VALUE = 'AutoAdd'

export enum PortfolioTrackerDisplayStage {
  Preparing = 'preparing',
  Shipped = 'shipped',
  OutForDelivery = 'outForDelivery',
  Delivered = 'delivered',
  Billed = 'billed',
  Returned = 'returned'
}

// Maps the 9 portfolio-context `OrderStatus` values (per CoreAPI's
// `PortfolioStatuses` manifest) into 6 display stages. Returns `null` for any
// other `OrderStatus` value — defensive default; iteration 8's walk should
// never hit this for real fake-registry data.
//
// `PortfolioNotPaid` collapses into `Billed` pending Marketing confirmation
// (open item from the iteration spec's §"Open questions"). If Marketing later
// says it's a distinct UI state, add a 7th display stage.
export function resolveDisplayStage(
  orderStatusId: OrderStatus | number
): PortfolioTrackerDisplayStage | null {
  switch (orderStatusId) {
    case OrderStatus.Portfolio:
    case OrderStatus.PortfolioInProduction:
    case OrderStatus.PortfolioReview:
      return PortfolioTrackerDisplayStage.Preparing
    case OrderStatus.PortfolioShipped:
      return PortfolioTrackerDisplayStage.Shipped
    case OrderStatus.OutForDelivery:
      return PortfolioTrackerDisplayStage.OutForDelivery
    case OrderStatus.Delivered:
      return PortfolioTrackerDisplayStage.Delivered
    case OrderStatus.Paid:
    case OrderStatus.OrderCompleted:
    case OrderStatus.PortfolioNotPaid:
      return PortfolioTrackerDisplayStage.Billed
    case OrderStatus.PortfolioReturned:
      return PortfolioTrackerDisplayStage.Returned
    default:
      return null
  }
}

// Visibility predicate. Tracker is hidden globally when:
//   - No portfolio overlay (non-Portfolio prooftype or no portfolio order yet)
//   - `ExtendedDeadlineDate` has passed — this is the **hard cutoff** when
//     the auto-charge fires server-side. CoreAPI computes it as
//     `DeadlineDate + 30 days`; until CoreAPI ships the field directly,
//     Sunny16 computes it locally.
//   - The resolved display stage is `Billed` or `Returned` — both are
//     terminal states from the customer's POV; the modal content (heading
//     "Welcome To Your Cart!", "3 easy ways to purchase or return" block,
//     auto-charge legal copy) doesn't fit either case, and the cart-side
//     auto-add reconciliation must not fire (customer has already either
//     paid for or returned the portfolio).
//
// `DeadlineDate` is the **soft cutoff** — the earlier "offer ends" date.
// Past `DeadlineDate` but pre-`ExtendedDeadlineDate` is a grace window where
// the tracker stays visible (customer can still act before the hard
// auto-charge); the promotional-hero countdown re-targets
// `ExtendedDeadlineDate` during this Phase 2 window.
//
// Business confirmed 2026-06-02: terminal states (Billed + Returned) hide
// the tracker outright and skip auto-add. This supersedes the earlier
// MKT-002-pending MVP default of "hide for Returned only" — the same
// rationale now extends to Billed.
export function isPortfolioTrackerApplicable(
  detail: SeniorPortfolioDetail | null,
  now: Date = new Date()
): boolean {
  if (detail === null) return false
  if (detail.ExtendedDeadlineDate !== null && new Date(detail.ExtendedDeadlineDate) <= now) {
    return false
  }
  const stage = resolveDisplayStage(detail.StatusId)
  if (stage === PortfolioTrackerDisplayStage.Billed) return false
  if (stage === PortfolioTrackerDisplayStage.Returned) return false
  return true
}

// Returns the `ContextKey`s that apply to the given Senior portfolio detail.
// Used by `MarketLandingView` to populate the `activeContexts` argument passed
// to `getAvailableFeatureItemsByDependencies`, gating Contentful feature items
// whose `dependsOnContext` matches.
//
// Currently returns `['senior-portfolio-delivered']` only when the tracker is
// applicable AND the display stage is `Delivered` — pairs with Marketing copy
// like "It's already in your hands." Returns `[]` for all other states.
// Additional context keys can extend the return value as new gates are added.
export function getActiveSeniorPortfolioContexts(
  detail: SeniorPortfolioDetail | null
): ContextKey[] {
  if (detail === null) return []
  if (!isPortfolioTrackerApplicable(detail)) return []
  if (resolveDisplayStage(detail.StatusId) !== PortfolioTrackerDisplayStage.Delivered) return []
  return [ContextKey.SeniorPortfolioDelivered]
}

// True if the catalog bundle's underlying bundle carries the
// `CADY_PortfolioType` attribute (any value). Used by cart reconciliation to
// detect that a Senior package already includes a portfolio, so the standalone
// auto-add bundle is not duplicated on top of it.
export function catalogBundleContainsPortfolio(catalogBundle: CatalogBundleStorefrontDto): boolean {
  return (
    catalogBundle.bundle?.attributes?.some((a) => a.key === PORTFOLIO_TYPE_ATTRIBUTE_KEY) ?? false
  )
}

// Type predicate: drops catalog bundles whose underlying `bundle` is null
// from the filter pipeline AND narrows the surviving entries so downstream
// code can read `.bundle` without a null check.
function hasResolvedBundle(cb: CatalogBundleStorefrontDto): cb is CatalogBundleWithResolvedBundle {
  return cb.bundle !== null
}

// Finds the standalone bundle to auto-add for Portfolio-prooftype Seniors —
// the one tagged `CADY_Senior_Portfolio=AutoAdd` on the underlying bundle. On
// ambiguity (multiple matches in a single catalog), sorts by `bundleId` for a
// deterministic pick and reports a warning to New Relic so the catalog config
// drift is visible. Returns `null` when no match exists. The return type
// asserts the underlying `bundle` is populated — callers can read
// `result.bundle` directly without a null check.
export function findAutoAddPortfolioBundle(
  catalogBundles: readonly CatalogBundleStorefrontDto[]
): CatalogBundleWithResolvedBundle | null {
  const matches = catalogBundles
    .filter(hasResolvedBundle)
    .filter((cb) =>
      cb.bundle.attributes?.some(
        (a) =>
          a.key === SENIOR_PORTFOLIO_ATTRIBUTE_KEY && a.value === SENIOR_PORTFOLIO_AUTO_ADD_VALUE
      )
    )

  if (matches.length === 0) return null
  if (matches.length === 1) return matches[0]

  const sorted = [...matches].sort((a, b) => a.bundle.bundleId.localeCompare(b.bundle.bundleId))
  appErrorService.reportError(
    new Error(
      `Multiple catalog bundles tagged ${SENIOR_PORTFOLIO_ATTRIBUTE_KEY}=${SENIOR_PORTFOLIO_AUTO_ADD_VALUE}; falling back to deterministic pick`
    ),
    {
      matchCount: matches.length,
      matchedBundleIds: matches.map((m) => m.bundle.bundleId),
      chosenBundleId: sorted[0].bundle.bundleId
    }
  )
  return sorted[0]
}
